/**
 * Product Types
 * Matches backend product response structure
 */

export type ProductType =
  | 'digital'
  | 'physical_product'
  | 'live_event'
  | 'subscription'
  | 'service'
  | 'course';

/**
 * One option a shopper can pick (from `product.variants`).
 * `price` is the FULL unit price in minor units (sale or list price plus the
 * option's add-ons), and `formatted_price` is ready to show. Pass the whole
 * object to `cart().addItem(product, quantity, variant)`.
 */
export interface ProductVariant {
  /** The option the shopper picks (the sub-option when there is one). UUID string. */
  id: number | string;
  name: string;
  price: number;
  formatted_price?: string | null;
  /** The top-level option; equals `id` when the option has no sub-options. */
  variation_id?: number | string;
  /** Set only when the option is a sub-option (e.g. size 42 · Red). */
  sub_variant_id?: number | string | null;
  sold_out?: boolean;
  sku?: string;
  stock?: number | null;
  attributes?: Record<string, string>;
}

export interface ProductSubscription {
  id: number;
  interval: 'weekly' | 'monthly' | 'yearly';
  interval_count: number;
  price: number;
  trial_days?: number;
}

export interface SubscriptionFrequencyOption {
  value: string;
  label: string;
  discount: number;
  original_price?: number;
  final_price?: number;
  is_default: boolean;
}

export interface SubscriptionPlan {
  plan_key: string;
  display_name: string;
  description?: string;
  quantity: number;
  price: number;
  features: string[];
  is_default: boolean;
  sort_order: number;
  frequency_options: SubscriptionFrequencyOption[];
}

export interface SubscriptionOptions {
  subscription_available: boolean;
  subscription_mode: 'tiers_only' | 'plans' | 'plans_with_tiers';
  allow_one_time_purchase: boolean;
  pricing_options: SubscriptionFrequencyOption[];
  plans?: SubscriptionPlan[];
  benefits: string[];
  trial_available: boolean;
  trial_duration_days?: number;
  trial_price?: number;
  cancellation_policy: {
    allowed_anytime: boolean;
    notice_period_days: number;
    policy_text?: string;
  };
  auto_renewal: boolean;
}

export interface EventTicket {
  id: number;
  name: string;
  description?: string;
  price: number; // In cents
  currency: string;
  quantity_remaining: number;
  ticket_deadline?: string | null;
  is_sold_out: boolean;
}

export interface ProductEvent {
  id?: number;
  event_start_date?: string;
  event_start_time?: string;
  event_end_date?: string;
  event_end_time?: string;
  event_location?: string;
  start_date?: string;
  end_date?: string;
  start_time?: string;
  duration?: number;
  timezone?: string;
  location?: string;
  meeting_url?: string;
  registration_deadline?: string;
  is_recurring?: boolean;
  status?: string;
  tickets: EventTicket[];
}

export interface Product {
  id: number;
  title: string;
  description?: string;
  price: number;
  formatted_price?: string; // Pre-formatted price from backend (e.g., "$100.00")
  slash_price?: number;
  formatted_slash_price?: string;
  is_slash_price: boolean;
  currency: string;
  type: ProductType;
  thumbnail?: string;
  images?: string[];
  is_live: boolean;
  stock?: number;
  variants?: ProductVariant[];
  subscriptions?: ProductSubscription[];
  subscription_options?: SubscriptionOptions;
  event?: ProductEvent;
  rating?: number;
  reviews_count?: number;
  badges?: string[];
  slug?: string;
}

export interface ProductQueryOptions {
  merchantId: string | number;
  currency?: string;
  productIds?: string;
  /**
   * NOTE: `category` is a backwards-compatibility alias for `type`
   * (digital/physical/etc.) on the backend — NOT a category filter.
   * To filter by an actual product category, use `categoryId` or
   * `categorySlug` below.
   */
  category?: string;
  /** Filter by numeric ProductCategory.id */
  categoryId?: number | string;
  /**
   * Filter by ProductCategory.slug or (case-insensitive) ProductCategory.name.
   * Comma-separated for multiple. Unioned with `categoryId` if both supplied.
   */
  categorySlug?: string;
  type?: ProductType;
  sort?: 'newest' | 'price_asc' | 'price_desc' | 'popular';
  limit?: number;
  offset?: number;
}

export interface ProductsResponse {
  products: Product[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

/**
 * Format price with currency
 */
export function formatPrice(
  amount: number | null | undefined,
  currency: string,
  locale: string = 'en-US'
): string {
  // Handle null/undefined/NaN amounts
  if (amount == null || isNaN(amount)) {
    return '—'; // Em dash for unavailable price
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Fallback for unsupported currencies
    return `${currency || 'USD'} ${amount.toFixed(2)}`;
  }
}

/**
 * Get product badge text
 */
export function getProductBadge(product: Product): string | null {
  if (product.is_slash_price && product.slash_price) {
    const discount = Math.round(((product.price - product.slash_price) / product.price) * 100);
    return `-${discount}%`;
  }
  if (product.type === 'live_event') return 'Event';
  if (product.type === 'subscription') return 'Subscription';
  if (product.badges?.[0]) return product.badges[0];
  return null;
}
